import './style.scss';

import { Controller, get } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputTextarea } from 'primereact/inputtextarea';
import { classNames } from 'primereact/utils';
import PropTypes from 'prop-types';
import { useRef, useCallback, useEffect } from 'react';

import { NON_PRINTABLE_REGEX } from 'constant';

export default function TextareaInput({
  label,
  name,
  placeholder,
  row,
  isRequired,
  disabled,
  control,
  errors,
  hidden,
  defaultValue,
  readOnly,
}) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  // Adjust height on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      adjustTextareaHeight();
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {label && (
        <label htmlFor={name} className="inline-block mb-2">
          {label}{' '}
          {isRequired && (
            <span className="text-red-500" style={{ fontWeight: 900 }}>
              *
            </span>
          )}
        </label>
      )}
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ref } }) => (
          <InputTextarea
            autoResize
            id={name}
            value={value ?? ''}
            placeholder={placeholder}
            onChange={(e) => {
              const formattedValue = e.target.value?.replace(NON_PRINTABLE_REGEX, '');
              onChange(formattedValue);
            }}
            rows={row}
            ref={(el) => {
              textareaRef.current = el;
              ref(el);
              if (el) {
                setTimeout(adjustTextareaHeight, 0);
              }
            }}
            className={classNames('relative ', {
              'p-invalid': !!get(errors, name),
              'surface-200': disabled,
              'opacity-100': readOnly,
            })}
            tooltip={t(get(errors, name)?.message)}
            tooltipOptions={{ position: 'bottom' }}
            disabled={disabled || readOnly}
            hidden={hidden}
          />
        )}
      />
    </>
  );
}

TextareaInput.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  row: PropTypes.number,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  hidden: PropTypes.bool,
  defaultValue: PropTypes.string,
};

TextareaInput.defaultProps = {
  label: '',
  placeholder: '',
  row: 1,
  isRequired: false,
  disabled: false,
  readOnly: false,
  hidden: false,
  defaultValue: '',
};
